import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2, ArrowLeft, FileCheck2, FileX, History, Truck, FileText, Download, Package, Camera } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { gov, GOV_API, govAuth } from '../../lib/govApi';
import axios from 'axios';
import { portalPath } from '../../lib/portal';
import { toast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import { statusBadge, statusLabel } from '../../lib/statusBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FINANCE_API = `${BACKEND_URL}/api/finance`;

const GovOrderDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, type: null, comment: '' });
  const [submitting, setSubmitting] = useState(false);
  const [fulfillment, setFulfillment] = useState({ status: 'processing', tracking_number: '', note: '' });
  const [delivery, setDelivery] = useState({
    courier: '', vehicle: '', driver: '', tracking_number: '', recipient_name: '',
    delivery_notes: '', pod_photos: [], signature: '',
  });
  const [deliverySaving, setDeliverySaving] = useState(false);
  const [deliveryOpen, setDeliveryOpen] = useState(false);

  const fetchOrder = useCallback(async () => {
    try {
      const r = await gov.getOrder(id);
      setOrder(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Order not found');
    }
  }, [id]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  const isUske = user && (user.role === 'super_admin' || user.role === 'admin');
  const isApprover = isUske || ['agency_admin', 'approver', 'finance_officer'].includes(user?.role);

  const submitDecision = async () => {
    setSubmitting(true);
    try {
      if (actionDialog.type === 'approve') {
        await gov.approve(order.order_id, actionDialog.comment);
        toast({ title: 'Approved' });
      } else {
        if (actionDialog.comment.trim().length < 3) {
          toast({ title: 'Reason required for rejection', description: 'Please enter at least 3 characters explaining why.', variant: 'destructive' });
          setSubmitting(false);
          return;
        }
        await gov.reject(order.order_id, actionDialog.comment);
        toast({ title: 'Rejected' });
      }
      setActionDialog({ open: false, type: null, comment: '' });
      fetchOrder();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Action failed', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const advanceStatus = async () => {
    setSubmitting(true);
    try {
      await gov.advanceStatus(order.order_id, fulfillment);
      toast({ title: 'Status updated', description: fulfillment.status });
      setFulfillment({ status: 'processing', tracking_number: '', note: '' });
      fetchOrder();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const fileToBase64 = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

  const onPodPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6);
    const bs = await Promise.all(files.map(fileToBase64));
    setDelivery(d => ({ ...d, pod_photos: [...d.pod_photos, ...bs].slice(0, 6) }));
  };

  const submitDelivery = async () => {
    if (!order) return;
    setDeliverySaving(true);
    try {
      const payload = {
        delivery_date: new Date().toISOString(),
        courier: delivery.courier || undefined,
        vehicle: delivery.vehicle || undefined,
        driver: delivery.driver || undefined,
        tracking_number: delivery.tracking_number || undefined,
        recipient_name: delivery.recipient_name || undefined,
        recipient_signature_url: delivery.signature || undefined,
        delivery_notes: delivery.delivery_notes || undefined,
        pod_photo_urls: delivery.pod_photos.length ? delivery.pod_photos : undefined,
      };
      const res = await axios.post(
        `${FINANCE_API}/orders/${order.order_id}/delivery`,
        payload,
      );
      toast({ title: 'Delivery recorded', description: `Delivery Note: ${res.data.delivery_note_no || ''}` });
      setDeliveryOpen(false);
      fetchOrder();
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || 'Delivery save failed', variant: 'destructive' });
    } finally {
      setDeliverySaving(false);
    }
  };

  if (err) return <div className="p-6 text-red-600">{err}</div>;
  if (!order) return <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-blue-700" /></div>;

  const canDownloadApproved = ['approved', 'processing', 'shipped', 'delivered'].includes(order.status);
  const canDownloadPackingSlip = ['processing', 'shipped', 'delivered'].includes(order.status);

  const downloadPdf = (kind) => {
    fetch(`${GOV_API}/orders/${order.order_id}/${kind}.pdf`, govAuth())
      .then(r => r.ok ? r.blob() : Promise.reject(r))
      .then(blob => {
        const u = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = u;
        a.download = `${kind}-${order.order_id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(u);
      })
      .catch(() => toast({ title: 'Download failed', description: `Could not download ${kind} PDF`, variant: 'destructive' }));
  };

  return (
    <div className="space-y-5" data-testid="gov-order-detail">
      <div className="flex items-center justify-between">
        <Link to={portalPath('/orders')} className="text-sm text-blue-700 inline-flex items-center gap-1"><ArrowLeft className="h-4 w-4" />Back to orders</Link>
        <div className="text-xs text-slate-400">Created {new Date(order.created_at).toLocaleString()}</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="border-2 lg:col-span-2">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <h1 className="text-xl font-bold text-slate-800">Order {order.order_id}</h1>
                <div className="text-sm text-slate-500">Agency: <b>{order.agency_name}</b> · PO #: <b>{order.po_number || '—'}</b></div>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs border ${statusBadge(order.status)}`} data-testid="gov-order-status">{statusLabel(order.status)}</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr><th className="text-left p-2">Item</th><th className="text-left p-2">SKU</th><th className="text-right p-2">Qty</th><th className="text-right p-2">Unit</th><th className="text-right p-2">Line</th></tr>
                </thead>
                <tbody>
                  {(order.items || []).map((it, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-2">{it.name}</td>
                      <td className="p-2 text-slate-500">{it.sku || '—'}</td>
                      <td className="p-2 text-right">{it.quantity}</td>
                      <td className="p-2 text-right">${Number(it.price).toFixed(2)}</td>
                      <td className="p-2 text-right font-semibold">${(Number(it.price) * Number(it.quantity)).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2"><td colSpan={4} className="p-2 text-right font-bold">Total</td><td className="p-2 text-right font-bold text-blue-700">${Number(order.total).toFixed(2)}</td></tr>
                </tfoot>
              </table>
            </div>

            {order.notes && (<div className="mt-3 bg-slate-50 p-3 rounded text-sm"><b>Purchaser notes:</b> {order.notes}</div>)}
            {order.department && (
              <div className="mt-2 text-sm text-slate-700" data-testid="gov-order-department">
                <b>Department:</b> {order.department.name}
                {order.department.code && <code className="ml-1 text-[10px] bg-slate-100 px-1.5 py-0.5 rounded border font-mono">{order.department.code}</code>}
              </div>
            )}
            {order.cost_center && (
              <div className="mt-2 text-sm text-slate-700" data-testid="gov-order-cost-center">
                <b>Cost Center:</b> <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded border font-mono">{order.cost_center.code}</code>
                {' '}— {order.cost_center.name}
                {order.cost_center.gl_account && <span className="text-slate-400 text-xs"> (GL {order.cost_center.gl_account})</span>}
              </div>
            )}
            {order.delivery_location && (
              <div className="mt-2 text-sm text-slate-700" data-testid="gov-order-delivery-location">
                <b>Delivery Location:</b> {order.delivery_location.name}
                <div className="text-xs text-slate-500 ml-4">
                  {order.delivery_location.address_line1}
                  {order.delivery_location.address_line2 ? `, ${order.delivery_location.address_line2}` : ''}
                  , {order.delivery_location.city}, {order.delivery_location.state} {order.delivery_location.zip}
                  {order.delivery_location.contact_name && <span> · Contact: {order.delivery_location.contact_name}</span>}
                </div>
                {order.delivery_location.delivery_instructions && (
                  <div className="text-xs italic text-slate-500 ml-4">Instructions: {order.delivery_location.delivery_instructions}</div>
                )}
              </div>
            )}
            {order.shipping_address && !order.delivery_location && (<div className="mt-2 text-sm text-slate-600"><b>Ship to:</b> {order.shipping_address}</div>)}
            {order.tracking_number && (<div className="mt-2 text-sm"><Truck className="inline h-4 w-4 mr-1" /><b>Tracking:</b> {order.tracking_number}</div>)}
          </CardContent>
        </Card>

        <div className="space-y-4">
          {/* Workflow Progress */}
          <Card className="border-2">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-800">Approval Workflow</h3>
                {order.status === 'pending_approval' && order.approvals?.length > 0 && (() => {
                  const cur = order.approvals.find((a) => (a.step || a.level) === (order.current_step || order.current_level));
                  return cur ? (
                    <span className="text-[11px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-semibold" data-testid="gov-current-role-badge">
                      Pending Approval — {cur.label || cur.role}
                    </span>
                  ) : null;
                })()}
                {order.status === 'approved' && <span className="text-[11px] bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-semibold">Fully Approved</span>}
                {order.status === 'sent_back' && <span className="text-[11px] bg-red-100 text-red-800 px-2 py-0.5 rounded-full font-semibold">Sent Back</span>}
              </div>

              {/* Horizontal stepper */}
              <div className="flex items-center justify-between gap-1 mb-3 overflow-x-auto pb-1" data-testid="gov-workflow-tracker">
                {(order.approvals || []).map((ap, idx, arr) => {
                  const stepNo = ap.step || ap.level;
                  const isCurrent = order.status === 'pending_approval' && stepNo === (order.current_step || order.current_level);
                  const color = ap.status === 'approved' ? 'bg-emerald-500 text-white'
                    : ap.status === 'rejected' ? 'bg-red-500 text-white'
                    : isCurrent ? 'bg-amber-500 text-white ring-2 ring-amber-300'
                    : 'bg-slate-200 text-slate-600';
                  return (
                    <React.Fragment key={stepNo}>
                      <div className="flex flex-col items-center flex-1 min-w-0" data-testid={`wf-tracker-step-${stepNo}`}>
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ${color}`}>
                          {ap.status === 'approved' ? '✓' : ap.status === 'rejected' ? '✕' : stepNo}
                        </div>
                        <div className="text-[10px] mt-1 text-center leading-tight text-slate-700 font-medium truncate w-full px-1">{ap.label || ap.role}</div>
                      </div>
                      {idx < arr.length - 1 && (
                        <div className={`h-0.5 flex-1 mt-4 min-w-[8px] ${ap.status === 'approved' ? 'bg-emerald-400' : 'bg-slate-200'}`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Detailed list */}
              <ol className="space-y-2 border-t pt-3" data-testid="gov-approval-chain">
                {(order.approvals || []).map((ap) => {
                  const stepNo = ap.step || ap.level;
                  const isCurrent = order.status === 'pending_approval' && stepNo === (order.current_step || order.current_level);
                  return (
                    <li key={stepNo} className="flex items-start gap-2 text-sm">
                      <span className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${ap.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : ap.status === 'rejected' ? 'bg-red-100 text-red-700' : isCurrent ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>{stepNo}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <b className="text-slate-800">{ap.label || ap.role}</b>
                          <span className="text-[10px] text-slate-400 uppercase">{ap.role}</span>
                        </div>
                        <div className="capitalize text-xs text-slate-600">{ap.status}{isCurrent && ap.status === 'pending' ? <span className="text-amber-700 font-semibold ml-1">← awaiting decision</span> : null}</div>
                        {(ap.approver_email || ap.approver_name) && <div className="text-xs text-slate-500">{ap.approver_name || ap.approver_email}{ap.decided_at ? ` · ${new Date(ap.decided_at).toLocaleString()}` : ''}</div>}
                        {ap.comment && <div className="text-xs italic text-slate-600">&quot;{ap.comment}&quot;</div>}
                      </div>
                    </li>
                  );
                })}
              </ol>
              {order.status === 'sent_back' && order.rejection_reason && (
                <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800" data-testid="gov-rejection-banner">
                  <b>Sent back at step {order.rejected_by_step}:</b> {order.rejection_reason}
                </div>
              )}
              {order.status === 'sent_back' && order.purchaser_email === user?.email && (
                <div className="mt-3 border-t pt-3">
                  <Button size="sm" className="w-full bg-blue-700 hover:bg-blue-800 text-white" onClick={async () => {
                    const note = window.prompt('Optional note to the first approver:', '') || '';
                    try {
                      await axios.post(`${GOV_API}/orders/${order.order_id}/resubmit`, { resubmit_note: note });
                      toast({ title: 'Resubmitted', description: 'Approval chain has been reset to step 1.' });
                      fetchOrder();
                    } catch (e) {
                      toast({ title: 'Resubmit failed', description: e.response?.data?.detail || '', variant: 'destructive' });
                    }
                  }} data-testid="gov-resubmit-btn">
                    <History className="h-4 w-4 mr-1" /> Resubmit for approval
                  </Button>
                  <p className="text-[10px] text-slate-400 text-center mt-1">Chain will restart at step 1. Resubmit count: {order.resubmission_count || 0}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Approver actions */}
          {order.status === 'pending_approval' && isApprover && (() => {
            const cur = (order.approvals || []).find((a) => (a.step || a.level) === (order.current_step || order.current_level));
            const requiredRole = cur?.role;
            const myTurn = requiredRole ? user?.role === requiredRole : true;
            return (
              <Card className={`border-2 ${myTurn ? 'border-blue-200' : 'border-slate-200'}`}>
                <CardContent className="p-4 space-y-2">
                  <h3 className="font-semibold text-slate-800">Decision</h3>
                  {myTurn ? (
                    <p className="text-xs text-slate-500">You are approving step <b>{order.current_step || order.current_level}</b> of {order.approvals?.length || 1} · <b>{cur?.label || cur?.role}</b>.</p>
                  ) : (
                    <p className="text-xs text-amber-700">This step requires the <b>{cur?.label || cur?.role}</b> role. You cannot act.</p>
                  )}
                  <div className="flex gap-2">
                    <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" disabled={!myTurn} onClick={() => setActionDialog({ open: true, type: 'approve', comment: '' })} data-testid="gov-approve-btn">
                      <FileCheck2 className="h-4 w-4 mr-1" /> Approve
                    </Button>
                    <Button className="flex-1" variant="outline" disabled={!myTurn} onClick={() => setActionDialog({ open: true, type: 'reject', comment: '' })} data-testid="gov-reject-btn">
                      <FileX className="h-4 w-4 mr-1" /> Reject
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* USKE fulfillment */}
          {isUske && ['approved', 'processing', 'shipped'].includes(order.status) && (
            <Card className="border-2">
              <CardContent className="p-4 space-y-2">
                <h3 className="font-semibold text-slate-800">Fulfillment (USKE)</h3>
                <Select value={fulfillment.status} onValueChange={(v) => setFulfillment({ ...fulfillment, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="processing">Processing</SelectItem>
                    <SelectItem value="shipped">Shipped</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                <Input placeholder="Tracking #" value={fulfillment.tracking_number} onChange={(e) => setFulfillment({ ...fulfillment, tracking_number: e.target.value })} />
                <Textarea rows={2} placeholder="Internal note (optional)" value={fulfillment.note} onChange={(e) => setFulfillment({ ...fulfillment, note: e.target.value })} />
                <Button onClick={advanceStatus} disabled={submitting} className="w-full bg-blue-700 hover:bg-blue-800 text-white">Update status</Button>
              </CardContent>
            </Card>
          )}

          {/* Delivery / POD */}
          {isUske && !['rejected', 'cancelled'].includes(order.status || '') && (
            <Card className="border-2 border-emerald-200">
              <CardContent className="p-4 space-y-2">
                <h3 className="font-semibold text-slate-800 flex items-center gap-1"><Truck className="h-4 w-4 text-emerald-700" /> Delivery / POD</h3>
                {order.delivery_note_no && (
                  <div className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                    Delivery Note <b>{order.delivery_note_no}</b>{order.courier ? ` · ${order.courier}` : ''}
                  </div>
                )}
                {!deliveryOpen ? (
                  <Button size="sm" variant="outline" className="w-full border-emerald-300 text-emerald-800" onClick={() => setDeliveryOpen(true)} data-testid="open-delivery-panel">
                    <Camera className="h-4 w-4 mr-1" /> Record delivery / upload POD
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <Input placeholder="Courier (e.g. UPS)" value={delivery.courier} onChange={(e) => setDelivery({ ...delivery, courier: e.target.value })} data-testid="delivery-courier" />
                    <Input placeholder="Vehicle / plate" value={delivery.vehicle} onChange={(e) => setDelivery({ ...delivery, vehicle: e.target.value })} data-testid="delivery-vehicle" />
                    <Input placeholder="Driver name" value={delivery.driver} onChange={(e) => setDelivery({ ...delivery, driver: e.target.value })} data-testid="delivery-driver" />
                    <Input placeholder="Tracking #" value={delivery.tracking_number} onChange={(e) => setDelivery({ ...delivery, tracking_number: e.target.value })} data-testid="delivery-tracking" />
                    <Input placeholder="Recipient name" value={delivery.recipient_name} onChange={(e) => setDelivery({ ...delivery, recipient_name: e.target.value })} data-testid="delivery-recipient" />
                    <Textarea rows={2} placeholder="Delivery notes" value={delivery.delivery_notes} onChange={(e) => setDelivery({ ...delivery, delivery_notes: e.target.value })} data-testid="delivery-notes" />
                    <div>
                      <Label className="text-xs">POD photos (max 6)</Label>
                      <input type="file" accept="image/*" multiple onChange={onPodPhotos} className="text-xs" data-testid="delivery-pod-input" />
                      {delivery.pod_photos.length > 0 && (
                        <div className="grid grid-cols-3 gap-1 mt-1">
                          {delivery.pod_photos.map((p, i) => (
                            <img key={i} src={p} alt={`pod-${i}`} className="h-14 w-full object-cover rounded border" />
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" variant="outline" onClick={() => setDeliveryOpen(false)} className="flex-1">Cancel</Button>
                      <Button size="sm" onClick={submitDelivery} disabled={deliverySaving} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="delivery-save">
                        {deliverySaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save POD'}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* PDF Downloads */}
          <Card className="border-2">
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-1"><FileText className="h-4 w-4" /> Documents</h3>
              <div className="flex flex-col gap-2">
                <Button variant="outline" size="sm" onClick={() => downloadPdf('po')} data-testid="download-po-pdf">
                  <Download className="h-4 w-4 mr-1" /> Purchase Order (PO)
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadPdf('approved')} disabled={!canDownloadApproved} data-testid="download-approved-pdf">
                  <FileCheck2 className="h-4 w-4 mr-1" /> Approved Order
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadPdf('packing-slip')} disabled={!canDownloadPackingSlip} data-testid="download-packing-slip-pdf">
                  <Package className="h-4 w-4 mr-1" /> Packing Slip
                </Button>
              </div>
              {!canDownloadApproved && <p className="text-[11px] text-slate-400 mt-2">Approved & Packing Slip PDFs unlock after approval.</p>}
            </CardContent>
          </Card>

          {/* Audit */}
          <Card className="border-2">
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-1"><History className="h-4 w-4" /> Audit Trail</h3>
              {(order.audit || []).length === 0 ? (
                <p className="text-xs text-slate-400">No events.</p>
              ) : (
                <ul className="space-y-2 text-xs">
                  {order.audit.map((a, i) => (
                    <li key={i} className="border-l-2 border-blue-200 pl-2" data-testid={`gov-audit-${i}`}>
                      <div className="font-semibold capitalize">{a.action.replace(/_/g, ' ')}</div>
                      <div className="text-slate-500">{a.actor_name || a.actor_email} · {new Date(a.timestamp).toLocaleString()}</div>
                      {a.comment && <div className="italic text-slate-600">&quot;{a.comment}&quot;</div>}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Decision dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(o) => !o && setActionDialog({ open: false, type: null, comment: '' })}>
        <DialogContent>
          <DialogHeader><DialogTitle>{actionDialog.type === 'approve' ? 'Approve' : 'Reject'} Order {order.order_id}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>{actionDialog.type === 'approve' ? 'Comment (optional)' : 'Reason for rejection *'}</Label>
            <Textarea rows={3} value={actionDialog.comment} onChange={(e) => setActionDialog({ ...actionDialog, comment: e.target.value })} data-testid="gov-decision-comment" />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setActionDialog({ open: false, type: null, comment: '' })}>Cancel</Button>
              <Button onClick={submitDecision} disabled={submitting} className={actionDialog.type === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'} data-testid="gov-decision-confirm">
                {submitting ? 'Working…' : (actionDialog.type === 'approve' ? 'Approve' : 'Reject')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GovOrderDetail;
